(() => {
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.collectionGrid) return;
  window.DigitalizModules.collectionGrid = true;

  // How far below the viewport the infinite-scroll sentinel triggers a load.
  // Used both as the IntersectionObserver bottom rootMargin and the chaining
  // re-check threshold, so loading starts before the user reaches the grid end.
  const INFINITE_ROOT_MARGIN = 800;

  function getOpenFilterKeys(root) {
    return [...root.querySelectorAll("details[data-filter-key][open]")]
      .map((node) => node.dataset.filterKey)
      .filter(Boolean);
  }

  function applyOpenFilterKeys(root, keys) {
    if (!Array.isArray(keys) || !keys.length) return;
    const keep = new Set(keys);
    root.querySelectorAll("details[data-filter-key]").forEach((node) => {
      const key = node.dataset.filterKey;
      if (key && keep.has(key)) node.setAttribute("open", "");
    });
  }

  function setLoadingState(root, loading) {
    root.dataset.loading = loading ? "true" : "false";
    root.classList.toggle("is-loading", loading);

    root
      .querySelectorAll("button, input, select, textarea")
      .forEach((field) => {
        if (!(field instanceof HTMLElement)) return;
        if (loading) {
          field.dataset.prevDisabled = field.hasAttribute("disabled") ? "true" : "false";
          field.setAttribute("disabled", "");
        } else if (field.dataset.prevDisabled === "false") {
          field.removeAttribute("disabled");
        }
      });
  }

  function buildSectionUrl(url, sectionId) {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set("section_id", sectionId);
    return nextUrl.toString();
  }

  async function fetchSectionHtml(url, sectionId) {
    const response = await fetch(buildSectionUrl(url, sectionId), {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });
    if (!response.ok) {
      throw new Error(`Collection fetch failed: ${response.status}`);
    }
    return response.text();
  }

  function parseRootFromHtml(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector(`[data-collection-section-root="${sectionId}"]`);
  }

  // Fire shopify:section:load on the swapped/appended subtree (not on document),
  // so target-scoped listeners (product cards, sliders) only re-initialise the
  // new content. The event bubbles, so document-level listeners still run once.
  function notifySectionLoad(target) {
    if (!target) return;
    target.dispatchEvent(new Event("shopify:section:load", { bubbles: true }));
  }

  async function replaceSection(root, url, options = {}) {
    const sectionId = root.dataset.collectionSectionId;
    if (!sectionId) return;

    const historyMode = options.historyMode || "push";
    const openFilterKeys = options.openFilterKeys || getOpenFilterKeys(root);

    setLoadingState(root, true);
    try {
      const html = await fetchSectionHtml(url, sectionId);
      const nextRoot = parseRootFromHtml(html, sectionId);
      if (!nextRoot) return;

      applyOpenFilterKeys(nextRoot, openFilterKeys);

      // Drop the observer bound to the outgoing root so it doesn't linger on a
      // detached sentinel.
      if (root._infiniteObserver) {
        root._infiniteObserver.disconnect();
        root._infiniteObserver = null;
      }

      root.replaceWith(nextRoot);

      if (historyMode === "push") {
        window.history.pushState({}, "", url);
      } else if (historyMode === "replace") {
        window.history.replaceState({}, "", url);
      }

      // Re-bind our own filter/sort/infinite handlers on the new root directly,
      // then let other components initialise just this subtree.
      initRoot(nextRoot);
      notifySectionLoad(nextRoot);
    } finally {
      setLoadingState(root, false);
    }
  }

  function onFiltersClick(event, root) {
    if (root.dataset.ajaxFilters !== "true") return;
    const link = event.target.closest("a[data-collection-filter-link]");
    if (!link) return;

    event.preventDefault();
    if (root.dataset.loading === "true") return;
    replaceSection(root, link.href, { historyMode: "push" });
  }

  function onFiltersSubmit(event, root) {
    if (root.dataset.ajaxFilters !== "true") return;

    const form = event.target.closest("form[data-collection-sort-form], form[data-collection-price-form]");
    if (!form) return;

    event.preventDefault();
    if (root.dataset.loading === "true") return;

    const action = form.getAttribute("action") || window.location.pathname;
    const url = new URL(action, window.location.origin);
    const params = new URLSearchParams(new FormData(form));
    url.search = params.toString();

    replaceSection(root, url.toString(), { historyMode: "push" });
  }

  async function loadMore(root, button) {
    const sectionId = root.dataset.collectionSectionId;
    const nextUrl = button?.dataset.nextUrl;
    if (!sectionId || !nextUrl || root.dataset.loading === "true") return;

    setLoadingState(root, true);
    button.disabled = true;

    try {
      const html = await fetchSectionHtml(nextUrl, sectionId);
      const doc = new DOMParser().parseFromString(html, "text/html");
      const currentGrid = root.querySelector(`[data-collection-grid="${sectionId}"]`);
      const nextGrid = doc.querySelector(`[data-collection-grid="${sectionId}"]`);

      if (!currentGrid || !nextGrid) return;

      [...nextGrid.querySelectorAll("[data-product-card-item]")].forEach((item) => {
        currentGrid.appendChild(item);
      });

      const currentControls = root.querySelector("[data-infinite-controls]");
      const nextButton = doc.querySelector("[data-load-more]");

      if (nextButton && currentControls) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl || "";
        button.disabled = false;
      } else if (currentControls) {
        // No more pages — remove the controls and stop observing the (now
        // detached) sentinel.
        currentControls.remove();
        if (root._infiniteObserver) {
          root._infiniteObserver.disconnect();
          root._infiniteObserver = null;
        }
      }

      // Initialise only the newly appended cards (scoped to the grid); the event
      // bubbles to document so cart and other document-level listeners run once.
      notifySectionLoad(currentGrid);
    } finally {
      setLoadingState(root, false);
    }
  }

  // Load the next page, then keep loading while the sentinel is still within the
  // trigger zone and more pages remain — so scrolling quickly past the grid (e.g.
  // straight into the sections below it) can't leave pages unloaded.
  async function maybeLoadMore(root) {
    const button = root.querySelector("[data-load-more]");
    if (!button || !button.dataset.nextUrl || root.dataset.loading === "true") return;

    await loadMore(root, button);

    const sentinel = root.querySelector("[data-infinite-sentinel]");
    const nextButton = root.querySelector("[data-load-more]");
    if (!sentinel || !nextButton || !nextButton.dataset.nextUrl) return;
    if (sentinel.getBoundingClientRect().top <= window.innerHeight + INFINITE_ROOT_MARGIN) {
      maybeLoadMore(root);
    }
  }

  function bindInfiniteScroll(root) {
    if (root.dataset.paginationMode !== "infinite_scroll") return;

    const button = root.querySelector("[data-load-more]");
    if (button && button.dataset.bound !== "true") {
      button.dataset.bound = "true";
      button.addEventListener("click", () => loadMore(root, button));
    }

    const sentinel = root.querySelector("[data-infinite-sentinel]");
    if (!sentinel || sentinel.dataset.bound === "true") return;

    sentinel.dataset.bound = "true";
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore(root);
      },
      // Trigger before the sentinel reaches the viewport so loading starts as
      // the user approaches the grid end, not only when its bottom is on screen.
      { rootMargin: `0px 0px ${INFINITE_ROOT_MARGIN}px 0px` }
    );
    observer.observe(sentinel);
    root._infiniteObserver = observer;
  }

  function initRoot(root) {
    if (!root || root.dataset.bound === "true") return;
    root.dataset.bound = "true";

    root.addEventListener("click", (event) => onFiltersClick(event, root));
    root.addEventListener("submit", (event) => onFiltersSubmit(event, root));

    bindInfiniteScroll(root);
  }

  function init() {
    document.querySelectorAll("[data-collection-section-root]").forEach(initRoot);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
  window.addEventListener("popstate", () => {
    const root = document.querySelector("[data-collection-section-root]");
    if (!root || root.dataset.loading === "true") return;
    if (root.dataset.ajaxFilters !== "true") return;
    replaceSection(root, window.location.href, { historyMode: "none" });
  });
})();
